/**
 * /compare/:slug — deep dive page per competitor.
 */
import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ExternalLink,
  Minus,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { track } from "@/lib/analytics";
import {
  ASSIGNFLOW,
  COMPETITORS_BY_SLUG,
  FEATURE_GROUPS,
  RIVALS,
} from "./competitors";
import MarketingShell from "./MarketingShell";

export default function CompareDetail() {
  const { slug } = useParams();
  const rival = COMPETITORS_BY_SLUG[slug];
  useEffect(() => {
    if (rival) track("compare_detail_view", { rival: rival.slug });
  }, [rival]);
  if (!rival || rival.self) return <Navigate to="/compare" replace />;

  return (
    <MarketingShell active="Compare">
      <Hero rival={rival} />
      <StrengthsSplit rival={rival} />
      <MatrixSection rival={rival} />
      <BestFor rival={rival} />
      <OtherComparisons currentSlug={rival.slug} />
      <BottomCTA rival={rival} />
    </MarketingShell>
  );
}

function Hero({ rival }) {
  return (
    <section className="mkt-section" style={{ paddingBottom: 28 }}>
      <div className="max-w-3xl">
        <div className="mkt-eyebrow"><ShieldCheck size={12} className="inline mr-1" /> An honest comparison</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]" data-testid="compare-detail-title">
          iSpeedToClose vs. <em className="text-[var(--primary-dark)] not-italic">{rival.name}</em>
        </h1>
        <p className="text-[var(--ink-2)] text-lg mt-5 leading-relaxed">{rival.summary}</p>
        <div className="flex flex-wrap gap-3 mt-7">
          <Link to="/register" className="btn-money" data-testid="compare-detail-cta-register">
            Try iSpeedToClose free <ArrowRight size={14} />
          </Link>
          {rival.site && (
            <a
              href={rival.site}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline inline-flex items-center gap-2"
              data-testid="compare-detail-rival-link"
            >
              Visit {rival.name} <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function StrengthsSplit({ rival }) {
  return (
    <section className="mkt-section" style={{ paddingTop: 24 }}>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="card p-7" data-testid="compare-rival-strengths">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-2">
            What {rival.name} does well
          </div>
          <h3 className="font-display font-bold text-2xl mb-5 leading-tight">Credit where it's due.</h3>
          <ul className="space-y-3">
            {rival.their_strengths.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[15px] text-[var(--ink-2)] leading-relaxed">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--ink-3)] shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card p-7 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="compare-our-strengths">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-2">
            Where iSpeedToClose wins
          </div>
          <h3 className="font-display font-bold text-2xl mb-5 leading-tight">The one-tab advantage.</h3>
          <ul className="space-y-3">
            {rival.where_we_complement.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[15px] text-[var(--ink-2)] leading-relaxed">
                <Check size={16} className="mt-0.5 text-emerald-600 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function MatrixSection({ rival }) {
  return (
    <section className="mkt-section" style={{ paddingTop: 8 }}>
      <div className="mb-6">
        <div className="mkt-eyebrow">Side-by-side</div>
        <h2 className="mkt-h2">Feature breakdown.</h2>
      </div>
      <div className="card overflow-x-auto" data-testid="compare-detail-matrix">
        <table className="w-full text-sm border-collapse min-w-[640px]">
          <thead>
            <tr>
              <th className="text-left p-4 font-display font-bold text-[var(--ink)] w-[50%] border-b border-[var(--line)]">Capability</th>
              <th className="p-4 text-center font-display font-bold text-emerald-800 bg-emerald-50 border-b border-[var(--line)]">
                iSpeedToClose
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-medium mt-0.5">
                  {ASSIGNFLOW.price_starting_label}
                </div>
              </th>
              <th className="p-4 text-center font-display font-bold text-[var(--ink)] border-b border-[var(--line)]">
                {rival.name}
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] font-medium mt-0.5">
                  {rival.price_starting_label}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {FEATURE_GROUPS.map((g) => (
              <FeatureGroup key={g.title} group={g} rival={rival} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function FeatureGroup({ group, rival }) {
  return (
    <>
      <tr>
        <td colSpan={3} className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
          {group.title}
        </td>
      </tr>
      {group.rows.map((r) => (
        <tr key={r.key} className="border-t border-[var(--line)]">
          <td className="p-4 text-[var(--ink-2)]">{r.label}</td>
          <td className="p-4 text-center align-middle bg-emerald-50/40">
            <Cell value={ASSIGNFLOW.features[r.key]} />
          </td>
          <td className="p-4 text-center align-middle">
            <Cell value={rival.features[r.key]} />
          </td>
        </tr>
      ))}
    </>
  );
}

function Cell({ value }) {
  if (!value || value.state === "no") {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--bg-2)] text-[var(--ink-3)]">
        <Minus size={13} />
      </span>
    );
  }
  if (value.state === "partial") {
    return (
      <span className="inline-flex flex-col items-center gap-0.5">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-700">
          <Check size={13} />
        </span>
        <span className="text-[10px] text-[var(--ink-3)] leading-tight max-w-[160px]">
          {value.note}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white shadow-sm">
        <Check size={13} strokeWidth={3} />
      </span>
      {value.note && (
        <span className="text-[10px] text-[var(--ink-3)] leading-tight max-w-[160px]">{value.note}</span>
      )}
    </span>
  );
}

function BestFor({ rival }) {
  return (
    <section className="mkt-section" style={{ paddingTop: 8 }}>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="card p-7" data-testid="compare-best-for-rival">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-3">Pick {rival.name} if…</div>
          <p className="text-[15px] text-[var(--ink-2)] leading-relaxed">{rival.ideal_for_them}</p>
        </div>
        <div className="card p-7 border-l-4 border-l-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="compare-best-for-us">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-3">Pick iSpeedToClose if…</div>
          <p className="text-[15px] text-[var(--ink-2)] leading-relaxed">{rival.ideal_for_us}</p>
        </div>
      </div>
    </section>
  );
}

function OtherComparisons({ currentSlug }) {
  const others = RIVALS.filter((r) => r.slug !== currentSlug);
  return (
    <section className="mkt-section" style={{ paddingTop: 8 }}>
      <div className="mb-6">
        <div className="mkt-eyebrow">More breakdowns</div>
        <h2 className="mkt-h2">Compare against another tool.</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {others.map((r) => (
          <Link
            key={r.slug}
            to={`/compare/${r.slug}`}
            className="mkt-feature group"
            data-testid={`compare-other-${r.slug}`}
          >
            <div className="font-display font-bold text-lg mb-1">vs. {r.name}</div>
            <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-3">{r.tagline}.</p>
            <div className="text-sm font-semibold text-[var(--ink)] flex items-center gap-2">
              Read <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function BottomCTA({ rival }) {
  return (
    <section className="mkt-section" style={{ paddingTop: 0 }}>
      <div className="relative overflow-hidden rounded-3xl bg-[var(--ink)] text-white p-14 lg:p-20">
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(60% 80% at 90% 30%, rgba(16,185,129,0.35) 0%, transparent 60%)" }}
        />
        <div className="relative z-10 max-w-2xl">
          <Sparkles size={28} className="text-[var(--primary)] mb-4" />
          <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">
            Run them in parallel — for free.
          </h2>
          <p className="text-white/75 text-lg mb-8 leading-relaxed">
            Keep using {rival.name} for what it does best. Try iSpeedToClose free for 14 days on the side and see what one tab feels like.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" className="btn-money" data-testid="compare-detail-bottom-cta">
              Start free <ArrowRight size={16} />
            </Link>
            <Link to="/compare" className="btn-outline" style={{ background: "rgba(255,255,255,0.06)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }}>
              See all comparisons
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
